import React, { useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import {
  LayoutFactory,
  PageLayoutType,
} from '../../../core/layout/LayoutFactory';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
  authTokenState,
  roleAccessState,
} from '../../../core/atoms/auth/auth.atom';
import LazyLoad from '../../../core/common/LazyLoading/LazyLoading';
import NotFoundPage from '../../../core/pages/error/NotFoundPage/NotFoundPage';
import { useMutation } from '@tanstack/react-query';
import axios, { SuccessResponse } from '../../../core/libraries/axios/axios';
import { MappedRoleAccess } from '../../../core/domain/Role';
import { DefaultError } from '@tanstack/query-core/build/modern/index';
import {
  ProgressFactory,
  ProgressVariant,
} from '../../../core/common/Progress/ProgressFactory';
import { apiRoutes } from '../../../config/api/apiRoutes';
import { routes } from '../../../config/routes/routes';

/* eslint-disable new-cap */
const Dashboard = LazyLoad(
  () => import('../../../app/pages/admin/DashboardPage/DashboardPage'),
);
const UsersPage = LazyLoad(
  () => import('../../../core/pages/admin/User/UsersPage/UsersPage'),
);
const UserCreatePage = LazyLoad(
  () => import('../../../core/pages/admin/User/UserCreatePage/UserCreatePage'),
);
const UserEditPage = LazyLoad(
  () => import('../../../core/pages/admin/User/UserEditPage/UserEditPage'),
);
const TranslationsPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/admin/Translation/TranslationsPage/TranslationsPage'
    ),
);
const ProfilePage = LazyLoad(
  () => import('../../../core/pages/admin/Profile/ProfilePage/ProfilePage'),
);
const SettingsPage = LazyLoad(
  () => import('../../../core/pages/admin/Settings/SettingsPage/SettingsPage'),
);

const LoginPage = LazyLoad(
  () => import('../../../core/pages/public/LoginPage/LoginPage'),
);
const PasswordResetPage = LazyLoad(
  () =>
    import('../../../core/pages/public/PasswordResetPage/PasswordResetPage'),
);
const PasswordResetConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/public/PasswordResetConfirmationPage/PasswordResetConfirmationPage'
    ),
);
const RegistrationConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/public/RegistrationConfirmationPage/RegistrationConfirmationPage'
    ),
);
const DeletionConfirmationPage = LazyLoad(
  () =>
    import(
      '../../../core/pages/public/DeletionConfirmationPage/DeletionConfirmationPage'
    ),
);

const EmployeesPage = LazyLoad(
  () => import('../../../app/pages/admin/Employee/EmployeesPage/EmployeesPage'),
);
const EmployeeCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Employee/EmployeeCreatePage/EmployeeCreatePage'
    ),
);
const EmployeeEditPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Employee/EmployeeEditPage/EmployeeEditPage'
    ),
);

const TravelersPage = LazyLoad(
  () => import('../../../app/pages/admin/Traveler/TravelersPage/TravelersPage'),
);
const TravelerCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Traveler/TravelerCreatePage/TravelerCreatePage'
    ),
);
const TravelerEditPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Traveler/TravelerEditPage/TravelerEditPage'
    ),
);
const TravelerViewPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Traveler/TravelerViewPage/TravelerViewPage'
    ),
);

const CompaniesPage = LazyLoad(
  () => import('../../../app/pages/admin/Company/CompaniesPage/CompaniesPage'),
);
const CompanyCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Company/CompanyCreatePage/CompanyCreatePage'
    ),
);
const CompanyEditPage = LazyLoad(
  () =>
    import('../../../app/pages/admin/Company/CompanyEditPage/CompanyEditPage'),
);
const CompanyViewPage = LazyLoad(
  () =>
    import('../../../app/pages/admin/Company/CompanyViewPage/CompanyViewPage'),
);

const TravelerManagerCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/TravelerManager/TravelerManagerCreatePage/TravelerManagerCreatePage'
    ),
);
const TravelerManagerEditPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/TravelerManager/TravelerManagerEditPage/TravelerManagerEditPage'
    ),
);

const EnquiriesPage = LazyLoad(
  () => import('../../../app/pages/admin/Order/EnquiriesPage/EnquiriesPage'),
);
const EnquiryEditPage = LazyLoad(
  () =>
    import('../../../app/pages/admin/Order/EnquiryEditPage/EnquiryEditPage'),
);

const QuotesPage = LazyLoad(
  () => import('../../../app/pages/admin/Order/QuotesPage/QuotesPage'),
);
const QuoteEditPage = LazyLoad(
  () => import('../../../app/pages/admin/Order/QuoteEditPage/QuoteEditPage'),
);

const AirportsPage = LazyLoad(
  () => import('../../../app/pages/admin/Airport/AirportsPage/AirportsPage'),
);
const AirlinesPage = LazyLoad(
  () => import('../../../app/pages/admin/Airline/AirlinesPage/AirlinesPage'),
);

const SuppliersPage = LazyLoad(
  () => import('../../../app/pages/admin/Supplier/SuppliersPage/SuppliersPage'),
);
const SupplierCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Supplier/SupplierCreatePage/SupplierCreatePage'
    ),
);
const SupplierEditPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Supplier/SupplierEditPage/SupplierEditPage'
    ),
);

const AccommodationsPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Accommodation/AccommodationsPage/AccommodationsPage'
    ),
);
const AccommodationCreatePage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Accommodation/AccommodationCreatePage/AccommodationCreatePage'
    ),
);
const AccommodationEditPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Accommodation/AccommodationEditPage/AccommodationEditPage'
    ),
);

const BookingsPage = LazyLoad(
  () => import('../../../app/pages/admin/Order/BookingsPage/BookingsPage'),
);
const BookingPage = LazyLoad(
  () => import('../../../app/pages/admin/Order/BookingPage/BookingPage'),
);

const OrdersOverviewPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/Order/OrdersOverviewPage/OrdersOverviewPage'
    ),
);

const CarHireSuppliersPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/CarHireSupplier/CarHireSuppliersPage/CarHireSuppliersPage'
    ),
);
const TransferVendorsPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/TransferVendor/TransferVendorsPage/TransferVendorsPage'
    ),
);
const RailSuppliersPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/RailSupplier/RailSuppliersPage/RailSuppliersPage'
    ),
);
const CruiseLinesPage = LazyLoad(
  () =>
    import(
      '../../../app/pages/admin/CruiseLine/CruiseLinesPage/CruiseLinesPage'
    ),
);
const CarTypesPage = LazyLoad(
  () => import('../../../app/pages/admin/CarType/CarTypesPage/CarTypesPage'),
);

const TasksPage = LazyLoad(
  () => import('../../../app/pages/admin/Task/TasksPage/TasksPage'),
);
/* eslint-enable new-cap */

export const RouteProvider = () => {
  const authStateValue = useRecoilValue(authTokenState);
  const setRoleAccessStateValue = useSetRecoilState(roleAccessState);

  const { mutate: getMappedRoleAccess, isPending } = useMutation<
    SuccessResponse<MappedRoleAccess[]>,
    DefaultError
  >({
    mutationFn: () => axios.get(apiRoutes.roles.mappedAccess),
    onSuccess: (response) => {
      setRoleAccessStateValue(response.data);
    },
  });

  useEffect(() => {
    if (authStateValue) {
      getMappedRoleAccess();
    }
  }, [authStateValue]);

  const adminPages = useMemo(() => {
    if (authStateValue) {
      return (
        <>
          <Route path={routes.admin.dashboard} element={<Dashboard />} />
          <Route path={routes.homepage} element={<Dashboard />} />
          <Route path={routes.admin.users.list} element={<UsersPage />} />
          <Route path={routes.admin.users.new} element={<UserCreatePage />} />
          <Route path={routes.admin.users.edit} element={<UserEditPage />} />
          <Route path={routes.admin.profile} element={<ProfilePage />} />
          <Route path={routes.admin.settings} element={<SettingsPage />} />
          <Route
            path={routes.admin.translations.list}
            element={<TranslationsPage />}
          />
          <Route
            path={routes.admin.employees.list}
            element={<EmployeesPage />}
          />
          <Route
            path={routes.admin.employees.new}
            element={<EmployeeCreatePage />}
          />
          <Route
            path={routes.admin.employees.edit}
            element={<EmployeeEditPage />}
          />
          <Route
            path={routes.admin.travelers.list}
            element={<TravelersPage />}
          />
          <Route
            path={routes.admin.travelers.new}
            element={<TravelerCreatePage />}
          />
          <Route
            path={routes.admin.travelers.edit}
            element={<TravelerEditPage />}
          />
          <Route
            path={routes.admin.travelers.view}
            element={<TravelerViewPage />}
          />
          <Route
            path={routes.admin.companiesV2.list}
            element={<CompaniesPage />}
          />
          <Route
            path={routes.admin.companiesV2.new}
            element={<CompanyCreatePage />}
          />
          <Route
            path={routes.admin.companiesV2.edit}
            element={<CompanyEditPage />}
          />
          <Route
            path={routes.admin.companiesV2.view}
            element={<CompanyViewPage />}
          />
          <Route
            path={routes.admin.travelerManagers.new}
            element={<TravelerManagerCreatePage />}
          />
          <Route
            path={routes.admin.travelerManagers.edit}
            element={<TravelerManagerEditPage />}
          />
          <Route
            path={routes.admin.enquiries.list}
            element={<EnquiriesPage />}
          />
          <Route
            path={routes.admin.enquiries.edit}
            element={<EnquiryEditPage />}
          />
          <Route
            path={routes.admin.suppliers.list}
            element={<SuppliersPage />}
          />
          <Route
            path={routes.admin.suppliers.new}
            element={<SupplierCreatePage />}
          />
          <Route
            path={routes.admin.suppliers.edit}
            element={<SupplierEditPage />}
          />
          <Route
            path={routes.admin.accommodations.list}
            element={<AccommodationsPage />}
          />
          <Route
            path={routes.admin.accommodations.new}
            element={<AccommodationCreatePage />}
          />
          <Route
            path={routes.admin.accommodations.edit}
            element={<AccommodationEditPage />}
          />
          <Route path={routes.admin.quotes.list} element={<QuotesPage />} />
          <Route path={routes.admin.quotes.edit} element={<QuoteEditPage />} />
          <Route path={routes.admin.bookings.list} element={<BookingsPage />} />
          <Route path={routes.admin.bookings.view} element={<BookingPage />} />
          <Route
            path={routes.admin.orders.overview}
            element={<OrdersOverviewPage />}
          />
          <Route
            path={routes.admin.carHireSuppliers.list}
            element={<CarHireSuppliersPage />}
          />
          <Route
            path={routes.admin.transferVendors.list}
            element={<TransferVendorsPage />}
          />
          <Route
            path={routes.admin.railSuppliers.list}
            element={<RailSuppliersPage />}
          />
          <Route
            path={routes.admin.cruiseLines.list}
            element={<CruiseLinesPage />}
          />
          <Route path={routes.admin.carTypes.list} element={<CarTypesPage />} />
          <Route path={routes.admin.airports.list} element={<AirportsPage />} />
          <Route path={routes.admin.airlines.list} element={<AirlinesPage />} />
          <Route path={routes.admin.tasks.list} element={<TasksPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </>
      );
    }

    return <></>;
  }, [authStateValue]);

  const authPages = useMemo(() => {
    if (authStateValue) {
      return (
        <>
          <Route
            path={routes.auth.registrationConfirmation}
            element={<RegistrationConfirmationPage />}
          />
          <Route
            path={routes.auth.deletionConfirmation}
            element={<DeletionConfirmationPage />}
          />
        </>
      );
    }

    return (
      <>
        <Route
          path={routes.auth.registrationConfirmation}
          element={<RegistrationConfirmationPage />}
        />
        <Route
          path={routes.auth.deletionConfirmation}
          element={<DeletionConfirmationPage />}
        />
        <Route
          path={routes.auth.passwordReset}
          element={<PasswordResetPage />}
        />
        <Route
          path={routes.auth.passwordResetConfirmation}
          element={<PasswordResetConfirmationPage />}
        />
        <Route path={routes.auth.login} element={<LoginPage />} />
        <Route path={routes.homepage} element={<LoginPage />} />
        <Route path="*" element={<Navigate to={routes.auth.login} replace />} />
      </>
    );
  }, [authStateValue]);

  if (authStateValue && isPending) {
    return <ProgressFactory variant={ProgressVariant.linear} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route element={<LayoutFactory variant={PageLayoutType.admin} />}>
          {adminPages}
        </Route>
        <Route element={<LayoutFactory variant={PageLayoutType.auth} />}>
          {authPages}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default RouteProvider;
